/**
 * Packs the pattern presets into uPatternData, PATTERN_ROWS vec4s a preset at its id's rows, which
 * patterns.glsl reads by the PS_* slots mirrored here. Sizes are metres; a module's variation is a
 * share of the module; colours are linear RGB, or a factor on the surface's own colour when the
 * slot after them says relative.
 */

import { Vector4, type IUniform } from 'three';
import type { PaletteModel } from '../../data/types';
import { hexToLinear, type LinearRGB } from '../../core/color';
import { WORLD } from '../../core/config';
import { PATTERN, PATTERN_COUNT } from './patternIds';
import { paintSectorPx } from './lineCover';
import { QUALITY_TIERS } from '../quality';
import type {
  BoardsPreset,
  BrickPreset,
  HollePanPreset,
  LeadPreset,
  LeadedGlassPreset,
  PatternColour,
  PatternKind,
  PatternPreset,
  PavingPreset,
  PilingPreset,
  PlankingPreset,
  Range,
  SailclothPreset,
  SlatePreset,
} from './presetTypes';

export const PATTERN_ROWS = 6;

export const PATTERN_SLOT = {
  kind: 0,
  moduleU: 1,
  moduleV: 2,
  joint: 3,
  jitterU: 4,
  jitterV: 5,
  tone: 6,
  variant: 7,
  accentShare: 8,
  aux1: 9,
  aux2: 10,
  aux3: 11,
  accent: 12,
  accentRelative: 15,
  band: 16,
  bandRelative: 19,
  bandEvery: 20,
  bandCourses: 21,
  bandCornerMin: 22,
  bandCornerMax: 23,
} as const;

export const PATTERN_KIND_CODE: Readonly<Record<PatternKind, number>> = {
  brick: 1,
  boards: 2,
  hollePan: 3,
  slate: 4,
  lead: 5,
  leadedGlass: 6,
  planking: 7,
  sailcloth: 8,
  piling: 9,
  paving: 10,
};

type Slots = Partial<Record<keyof typeof PATTERN_SLOT, number>>;

/** A preset for every id but none, as presets.ts gives them. */
export type PresetTable = Readonly<Record<Exclude<keyof typeof PATTERN, 'none'>, PatternPreset>>;

interface Packed {
  readonly slots: Slots;
  readonly accent: PatternColour;
  readonly band?: PatternColour;
}

const mid = (r: Range): number => (r[0] + r[1]) / 2;
/* Each boundary moves up to a quarter of the range either way, so a module spans the whole range. */
const boundaryJitter = (r: Range, module: number): number => (r[1] - r[0]) / 4 / module;

const BOND_CODE = { staand: 0, kruis: 1, ashlar: 2 } as const;

const courseRange = (every: number | Range): Range =>
  typeof every === 'number' ? [every, every] : every;

function brickSlots(p: BrickPreset): Packed {
  const moduleU = mid(p.brickLengthM) + p.jointM;
  const moduleV = mid(p.brickHeightM) + p.jointM;
  const bed = p.jointM / moduleV;
  /* Half the courses are headers, half a module long, but in ashlar every course is of stretchers. */
  const perpends =
    p.bond === 'ashlar' ? p.jointM / moduleU : (p.jointM / moduleU + p.jointM / (moduleU / 2)) / 2;
  const band = p.band;
  return {
    slots: {
      moduleU,
      moduleV,
      joint: p.jointM,
      jitterU: boundaryJitter(p.brickLengthM, moduleU),
      jitterV: boundaryJitter(p.brickHeightM, moduleV),
      variant: BOND_CODE[p.bond],
      accentShare: bed + (1 - bed) * perpends,
      aux1: p.closers ? 1 : 0,
      bandEvery: band ? courseRange(band.everyCourses)[0] + band.courses : 0,
      aux2: band ? courseRange(band.everyCourses)[1] + band.courses : 0,
      bandCourses: band?.courses ?? 0,
      bandCornerMin: band?.cornerLengthM?.[0] ?? 0,
      bandCornerMax: band?.cornerLengthM?.[1] ?? 0,
    },
    accent: p.mortar,
    ...(band ? { band: band.colour } : {}),
  };
}

function boardsSlots(p: BoardsPreset): Packed {
  return {
    slots: {
      moduleU: p.boardWidthM,
      joint: p.jointM,
      jitterU: p.widthVariation / 2,
      accentShare: p.jointM / p.boardWidthM,
    },
    accent: p.joint,
  };
}

function hollePanSlots(p: HollePanPreset): Packed {
  return {
    slots: {
      moduleU: p.coverWidthM,
      moduleV: p.gaugeM,
      joint: p.courseWidthM,
      variant: p.lapping === 'left' ? 1 : 0,
      accentShare: p.courseWidthM / p.gaugeM,
      aux1: p.rollContrast,
    },
    accent: p.course,
  };
}

function slateSlots(p: SlatePreset): Packed {
  const shadow = p.courseShadowM / p.courseM;
  return {
    slots: {
      moduleU: p.slateWidthM,
      moduleV: p.courseM,
      joint: p.jointM,
      accentShare: shadow + (1 - shadow) * (p.jointM / p.slateWidthM),
      aux1: p.courseShadowM,
    },
    accent: p.courseShadow,
  };
}

function leadSlots(p: LeadPreset): Packed {
  const rolls = p.rollWidthM / p.bayWidthM;
  return {
    slots: {
      moduleU: p.bayWidthM,
      moduleV: p.sheetLengthM,
      joint: p.rollWidthM,
      accentShare: rolls + (1 - rolls) * (p.rollWidthM / p.sheetLengthM),
    },
    accent: p.roll,
  };
}

/* Two families of came lines cross the diamonds; each runs a quarry's diagonal apart. */
function leadedGlassSlots(p: LeadedGlassPreset): Packed {
  const spacing = (p.quarryWidthM * p.quarryHeightM) / Math.hypot(p.quarryWidthM, p.quarryHeightM);
  const family = p.cameWidthM / spacing;
  return {
    slots: {
      moduleU: p.quarryWidthM,
      moduleV: p.quarryHeightM,
      joint: p.cameWidthM,
      accentShare: 1 - (1 - family) * (1 - family),
      aux1: p.saddleBarM,
    },
    accent: p.came,
  };
}

function plankingSlots(p: PlankingPreset): Packed {
  const plank = mid(p.plankLengthM);
  return {
    slots: {
      moduleU: plank,
      joint: p.buttWidthM,
      jitterU: (p.plankLengthM[1] - p.plankLengthM[0]) / 2 / plank,
      variant: p.lap === 'clinker' ? 1 : 0,
      accentShare: p.seamFraction,
      aux1: p.waterlineShade,
      aux2: p.waterlineDepthM,
      aux3: WORLD.waterY,
    },
    accent: p.seam,
  };
}

function sailclothSlots(p: SailclothPreset): Packed {
  const cloth = mid(p.clothWidthM);
  return {
    slots: {
      moduleU: cloth,
      joint: p.seamWidthM,
      jitterU: boundaryJitter(p.clothWidthM, cloth),
      accentShare: p.seamWidthM / cloth,
      aux1: p.bonnetAt,
      aux2: p.lacingWidthM,
    },
    accent: p.seam,
  };
}

function pilingSlots(p: PilingPreset): Packed {
  return {
    slots: {
      moduleU: p.postSpacingM,
      joint: p.postWidthM,
      accentShare: p.postWidthM / p.postSpacingM,
    },
    accent: p.post,
  };
}

/* A front's pitch and each pair's outline are drawn from their ranges in the shader, so both ends
   go in; the share the outlines cover changes with the pitch on screen, so none is packed. */
function pavingSlots(p: PavingPreset): Packed {
  return {
    slots: {
      moduleU: p.pitchM[0],
      moduleV: p.pitchM[1],
      joint: p.outlinePx[0],
      aux1: p.outlinePx[1],
      aux2: p.outline.nearPitchPx,
      aux3: p.outline.farPitchPx,
    },
    accent: p.outline.near,
    band: p.outline.far,
  };
}

function slotsOf(preset: PatternPreset): Packed {
  switch (preset.kind) {
    case 'brick':
      return brickSlots(preset);
    case 'boards':
      return boardsSlots(preset);
    case 'hollePan':
      return hollePanSlots(preset);
    case 'slate':
      return slateSlots(preset);
    case 'lead':
      return leadSlots(preset);
    case 'leadedGlass':
      return leadedGlassSlots(preset);
    case 'planking':
      return plankingSlots(preset);
    case 'sailcloth':
      return sailclothSlots(preset);
    case 'piling':
      return pilingSlots(preset);
    case 'paving':
      return pavingSlots(preset);
  }
}

/** The colour and whether it is a factor on the surface's own colour. */
function resolveColour(colour: PatternColour, palette: PaletteModel): [LinearRGB, number] {
  if ('baseTimes' in colour) return [[colour.baseTimes, colour.baseTimes, colour.baseTimes], 1];
  if ('measured' in colour) return [hexToLinear(colour.measured), 0];
  return [palette[colour.paletteClass].median.linear, 0];
}

function writePreset(
  target: Float32Array,
  at: number,
  preset: PatternPreset,
  palette: PaletteModel
): void {
  const packed = slotsOf(preset);
  const values: Slots = {
    ...packed.slots,
    kind: PATTERN_KIND_CODE[preset.kind],
    tone: preset.toneSpread,
  };
  for (const [name, value] of Object.entries(values)) {
    target[at + PATTERN_SLOT[name as keyof typeof PATTERN_SLOT]] = value;
  }
  const [accent, accentRelative] = resolveColour(packed.accent, palette);
  target.set(accent, at + PATTERN_SLOT.accent);
  target[at + PATTERN_SLOT.accentRelative] = accentRelative;
  if (packed.band) {
    const [band, bandRelative] = resolveColour(packed.band, palette);
    target.set(band, at + PATTERN_SLOT.band);
    target[at + PATTERN_SLOT.bandRelative] = bandRelative;
  }
}

export function packPatternPresets(presets: PresetTable, palette: PaletteModel): Float32Array {
  const width = PATTERN_ROWS * 4;
  const data = new Float32Array(PATTERN_COUNT * width);
  for (const [name, preset] of Object.entries(presets)) {
    writePreset(data, PATTERN[name as keyof typeof presets] * width, preset, palette);
  }
  return data;
}

/* A module never drops below this share of the surface's colour: cream mortar is bluer than the
   brick, and paying for its blue in full would turn the bricks' blue to nothing. */
export const PATTERN_MODULE_FLOOR = 0.6;

/**
 * The colours of a surface's modules and joints whose mean, the joints taking `share` of the area,
 * is the surface's own colour: the joint takes the accent colour, less any channel the modules
 * could not pay for above PATTERN_MODULE_FLOOR. patterns.glsl's keptMean mirrors it.
 */
export function keptMean(
  base: LinearRGB,
  accent: LinearRGB,
  share: number
): { readonly module: LinearRGB; readonly joint: LinearRGB } {
  const channel = (c: 0 | 1 | 2): [number, number] => {
    const cap = (base[c] * (1 - PATTERN_MODULE_FLOOR * (1 - share))) / Math.max(share, 1e-4);
    const joint = Math.min(accent[c], cap);
    return [(base[c] - share * joint) / Math.max(1 - share, 1e-4), joint];
  };
  const [r, g, b] = [channel(0), channel(1), channel(2)];
  return { module: [r[0], g[0], b[0]], joint: [r[1], g[1], b[1]] };
}

export function patternDefines(): Record<string, number> {
  return { PATTERN_PRESETS: PATTERN_COUNT };
}

export interface PatternUniforms {
  uPatternData: IUniform<Vector4[]>;
  uPaintSectorPx: IUniform<number>;
}

export function createPatternUniforms(
  presets: PresetTable,
  palette: PaletteModel
): PatternUniforms {
  const data = packPatternPresets(presets, palette);
  const rows = Array.from({ length: data.length / 4 }, (_, i) =>
    new Vector4().fromArray(data, i * 4)
  );
  /* resize.ts names the tier before the first frame; until then, the default tier's sector. */
  const sector = paintSectorPx(QUALITY_TIERS.medium.kuwaharaRadius);
  return { uPatternData: { value: rows }, uPaintSectorPx: { value: sector } };
}
